<template>
    <div>
        <Panel
            :title="$t('Chat API Accounts')"
            :add-button="canEdit || canEditV2UI || canEditV3UI ? $t('Add Account') : ''"
            :delete-button="selected.length > 0"
            :total-rows="totalRows"
            :page="page"
            @delete="handleDelete"
            @input="openForm(-1)"
            @change="changePage"
        >
            <h3 class="header-left">{{ $t('API Accounts') }}</h3>
            <table id="chatapiaccounts_table" class="cic-table">
                <thead>
                    <tr>
                        <th>{{ $t('Name') }}</th>
                        <th>{{ $t('Chat API Key') }}</th>
                        <th>{{ $t('Chat API Secret') }}</th>
                        <th class="td-checkbox">{{ $t('Show Secret') }}</th>
                        <th></th>
                        <th v-if="canEdit" class="td-checkbox"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="account in chatApiAccounts">
                        <td class="primary no-wrap" @click="(evt) => handleOpen(account.accountId, evt)">
                            {{ account.accountName }}
                        </td>
                        <td>{{ account.apiKey }}</td>
                        <td>
                            <div v-if="secretVisible.indexOf(account.accountId) !== -1">{{ account.apiSecret }}</div>
                        </td>
                        <td>
                            <cic-checkbox
                                :value="secretVisible.indexOf(account.accountId) !== -1"
                                @click="showSecret(account.accountId)"
                            />
                        </td>
                        <td class="copy">
                            <cic-button class="copy-button" @click="copyValue(account.apiSecret)">{{
                                $t('Copy')
                            }}</cic-button>
                        </td>
                        <td v-if="canEdit">
                            <cic-checkbox
                                :value="selected.indexOf(account.accountId) !== -1"
                                @click="handleSelected(account.accountId)"
                            />
                        </td>
                    </tr>
                </tbody>
            </table>

            <h3 class="header-left">{{ $t('V2 User Interface Accounts') }}</h3>

            <table id="webchatv2accounts_table" class="cic-table">
                <thead>
                    <tr>
                        <th>{{ $t('Name') }}</th>
                        <th>{{ $t('Endpoint URL') }}</th>
                        <th></th>
                        <th v-if="canEditV2UI" class="td-checkbox"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="account in webChatV2Accounts">
                        <td class="primary no-wrap" @click="(evt) => handleOpen(account.accountId, evt)">
                            {{ account.accountName }}
                        </td>
                        <td>{{ account.userInterfaceUrl }}</td>
                        <td class="copy">
                            <cic-button class="copy-button" @click="copyValue(account.userInterfaceUrl)">{{
                                $t('Copy')
                            }}</cic-button>
                        </td>
                        <td v-if="canEditV2UI">
                            <cic-checkbox
                                :value="selected.indexOf(account.accountId) !== -1"
                                @click="handleSelected(account.accountId)"
                            />
                        </td>
                    </tr>
                </tbody>
            </table>

            <h3 class="header-left">{{ $t('V3 User Interface Accounts') }}</h3>

            <table id="webchatv3accounts_table" class="cic-table">
                <thead>
                <tr>
                    <th>{{ $t('Name') }}</th>
                    <th>{{ $t('Endpoint URL') }}</th>
                    <th></th>
                    <th v-if="canEditV3UI" class="td-checkbox"></th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="account in webChatV3Accounts">
                    <td class="primary no-wrap" @click="(evt) => handleOpen(account.accountId, evt)">
                        {{ account.accountName }}
                    </td>
                    <td>{{ account.userInterfaceUrl }}</td>
                    <td class="copy">
                        <cic-button class="copy-button" @click="copyValue(account.userInterfaceUrl)">{{
                                $t('Copy')
                            }}</cic-button>
                    </td>
                    <td v-if="canEditV3UI">
                        <cic-checkbox
                            :value="selected.indexOf(account.accountId) !== -1"
                            @click="handleSelected(account.accountId)"
                        />
                    </td>
                </tr>
                </tbody>
            </table>
        </Panel>

        <EditChatAPIAccount
            v-if="chatAccountDetails !== null && !$store.showLoadingOverlay"
            :chat-account-details="chatAccountDetails"
            :chat-parameters="chatParameters"
            :surveys="surveys"
            @change="updateFieldValue"
            @blur="closePopup"
            @input="saveChatAccountDetails"
        />

        <div v-if="$store.showLoadingOverlay" class="cic-overlay"></div>
    </div>
</template>

<script>
    import Panel from '@/components/container/Panel';
    import CicButton from '@/components/CicButton';
    import CicCheckbox from '@/components/inputs/CicCheckbox';
    import TableUtils from '@/mixins/TableUtils';
    import { save, load, deleteItems } from '@/utils/Http';
    import EditChatAPIAccount from './EditChatAPIAccount';
    import '@/css/socialmedia.css';

    export default {
        name: 'ChatAPIAccounts',
        components: {
            EditChatAPIAccount,
            Panel,
            CicCheckbox,
            CicButton,
        },
        mixins: [TableUtils],
        data() {
            return {
                accounts: [],
                chatApiAccounts: [],
                webChatV2Accounts: [],
                webChatV3Accounts: [],
                surveys: [],
                selected: [],
                secretVisible: [],
                page: 1,
                totalRows: 0,
                resultsPerPage: 20,
                chatAccountDetails: null,
                chatParameters: null,
            };
        },
        computed: {
            canEdit() {
                return this.$store.hasRight('EDIT_ACCESS_TYPE_26');
            },
            canEditV2UI() {
                return this.$store.hasRight('EDIT_ACCESS_TYPE_26_UI');
            },
            canEditV3UI() {
                return this.$store.hasRight('EDIT_ACCESS_TYPE_26_V3UI');
            },
        },
        created() {
            this.loadTable();
        },
        methods: {
            loadTable() {
                this.closePopup();
                load('chatapi/batch', { page: this.page }, (response) => {
                    this.accounts = response.accounts;
                    this.chatApiAccounts = this.accounts.filter((account) => account.userInterfaceUrl === null);
                    this.webChatV2Accounts = this.accounts.filter((account) => account.userInterfaceUrl !== null && !account.hasV3Interface);
                    this.webChatV3Accounts = this.accounts.filter((account) => account.userInterfaceUrl !== null && account.hasV3Interface);
                    this.surveys = response.surveys;
                    this.totalRows = response.totalRows;
                    this.orgParameters = response.parameters; // Parameters may be null if ChatAPI not upgraded, or may be empty list if none were found for that org

                    // Parameters require TranscriptSettings string parsed to object
                    if (this.orgParameters) {
                        this.orgParameters.forEach(param => {
                            if (param.TranscriptSettings) {
                                try {
                                    param.TranscriptSettings = JSON.parse(param.TranscriptSettings);
                                } catch (error) {
                                    console.error('Invalid JSON in TranscriptSettings');
                                    param.TranscriptSettings = {};
                                }
                            }
                        });
                    }

                    while (this.page > Math.ceil(this.totalRows / this.resultsPerPage) && this.page !== 1) {
                        this.page = this.page - 1;
                        this.loadTable();
                    }
                });
            },
            openForm(accountId) {
                if (accountId > 0) {
                    this.chatAccountDetails = this.accounts.find((chatAccountDetails) => chatAccountDetails.accountId === accountId);

                    // Determine the chat parameters for the given account
                    if (!this.orgParameters) {
                        this.chatParameters = null;
                    } else {
                        // Attempt to find parameters for the account
                        this.chatParameters = this.orgParameters.find(param => param.NumberManagementAccountId === accountId);

                        if (!this.chatParameters) {
                            // If no parameters found for the account, use default values
                            this.chatParameters = {
                                NumberManagementAccountId: accountId,
                                AllowAttachments: false,
                                ClickableUrls: false,
                                ShowAgentsName: true,
                                AllowEmailTranscript: false,
                                TranscriptSettings: {},
                            };
                        }
                    }

                    if (this.surveys.length) this.loadCsatOptions();
                } else {
                    this.chatAccountDetails = {
                        accountId: null,
                        accountName: '',
                        surveyId: '',
                        thankYouMessage: '',
                        hasUserInterface: false,
                        hasV3Interface: false,
                        webhookUrl: '',
                        subscribedEvents: [],
                    };
                    if (!this.orgParameters) {
                        this.chatParameters = null;
                    } else {
                        this.chatParameters = {
                            NumberManagementAccountId: null,
                            AllowAttachments: false,
                            ClickableUrls: false,
                            ShowAgentsName: true,
                            AllowEmailTranscript: false,
                            TranscriptSettings: {},
                        };
                    }
                }
            },
            updateFieldValue(object, field, value) {
                if (object === 'chatAccountDetails') {
                    let tmp = { ...this.chatAccountDetails };
                    tmp[field] = value;
                    this.chatAccountDetails = tmp;
                } else if (object === 'chatParameters') {
                    let tmp = { ...this.chatParameters };
                    tmp[field] = value;
                    this.chatParameters = tmp;
                }
            },
            closePopup() {
                this.chatAccountDetails = null;
                this.chatParameters = null;
            },
            saveChatAccountDetails() {
                this.page = 1;
                if (this.chatAccountDetails.accountId > 0) {
                    save(
                        'chatapi/update',
                        {
                            ...this.chatAccountDetails,
                            ...(this.chatParameters || {}),
                            ...(this.chatParameters?.TranscriptSettings || {}),
                        },
                        this.loadTable,
                    );

                } else {
                    save(
                        'chatapi/create',
                        {
                            ...this.chatAccountDetails,
                            ...(this.chatParameters || {}),
                            ...(this.chatParameters?.TranscriptSettings || {}),
                            hasUserInterface: this.chatAccountDetails.hasUserInterface ? 1 : 0,
                            hasV3Interface: this.chatAccountDetails.hasV3Interface ? 1 : 0,
                        },
                        this.loadTable,
                    );
                }
            },
            loadCsatOptions() {
                load('chatapi/getCsat', { accountId: this.chatAccountDetails.accountId }, (response) => {
                    this.chatAccountDetails.surveyId = response.surveyId;
                    this.chatAccountDetails.thankYouMessage = response.thankYouMessage;
                });
            },
            handleDelete() {
                deleteItems('chatapi/delete', { accountIds: this.selected }, this.loadTable);
            },
            showSecret(accountId) {
                if (this.secretVisible.indexOf(accountId) !== -1) {
                    this.secretVisible.splice(this.secretVisible.indexOf(accountId), 1);
                } else {
                    this.secretVisible.push(accountId);
                }
                this.$forceUpdate();
            },
            copyValue(value) {
                const elem = document.createElement('textarea');
                document.body.appendChild(elem);
                elem.value = value;
                elem.select();
                document.execCommand('copy');
                document.body.removeChild(elem);
            },
        },
    };
</script>
